import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Map as MapLibreMap, MapGeoJSONFeature } from 'maplibre-gl';

import { ChartView } from './chart/ChartView';
import {
  ChartLoadError,
  formatScale,
  loadChartCollections,
  maxChartedZoom,
  mergeCollections,
} from './chart/chartSource';
import {
  type RouteCheckResult,
  checkRouteAgainstChart,
} from './chart/routeCheck';
import {
  DEFAULT_AUTO_ROUTE_OPTIONS,
  describeAutoRouteFailure,
  planningViewportFor,
  suggestRouteBetween,
} from './chart/autoRoute';
import type { ChartManifest } from './chart/style';
import {
  type ForwardScan,
  describeForwardDanger,
} from './chart/forwardDangers';
import { type LatLon, boundsOf, destinationPoint } from './core/geo';
import {
  TINT_CLEAR_RADIUS_PCT,
  flashPeriodMs,
  isCollision,
  proximityAlert,
  tintColour,
  tintOpacity,
} from './core/proximity';
import { type Fix, safetyDepth } from './core/boat';
import {
  type Route,
  addWaypoint,
  createRoute,
  reverseRoute,
} from './core/route';
import {
  ShareDecodeError,
  decodeTokenToRoute,
  decodedToWaypoints,
  extractShareToken,
} from './core/share';
import { DISCLAIMER_VERSION, needsAcknowledgement } from './core/legal';
import { MIN_PREDICTION_SPEED_MS } from './core/predict';
import { type Webcam, webcamById } from './core/webcams';
import { type Harbour, harbourById } from './core/harbours';
import { formatDepth, formatLatitude, formatLongitude, formatSpeed } from './core/units';
import {
  type Track,
  appendPoint,
  createTrack,
  markSimulated,
  trackBounds,
} from './core/track';
import { deleteRoute, listRoutes, saveRoute } from './storage/routeStore';
import {
  type TrackSummary,
  deleteTrack,
  listTracks,
  loadTrack,
  saveTrack,
} from './storage/trackStore';
import { loadSettings, saveSettings, type Settings } from './storage/settings';
import { useGeolocation, useWakeLock, isPoorAccuracy, isStale } from './ui/useGeolocation';
import { useProximityAlarm } from './ui/proximityAlarm';
import { DisclaimerGate } from './ui/DisclaimerGate';
import { FeaturePanel } from './ui/FeaturePanel';
import { RoutePanel } from './ui/RoutePanel';
import { OfflinePanel } from './ui/OfflinePanel';
import { SettingsPanel } from './ui/SettingsPanel';
import { TrackPanel } from './ui/TrackPanel';
import { WebcamPanel } from './ui/WebcamPanel';
import { HarbourPanel } from './ui/HarbourPanel';
import type { Region } from './storage/chartCache';
import './app.css';

const newId = (): string => (globalThis.crypto?.randomUUID?.() ?? `id-${Math.random().toString(36).slice(2)}`);

/**
 * Stable empty array for "no track on the chart".
 *
 * A fresh `[]` on every render is a new prop identity, which re-runs the
 * ChartView effect that pushes track data into MapLibre on every frame.
 */
const NO_TRACK_POINTS: LatLon[] = [];

/**
 * Wait until the map has finished loading and rendering everything in view.
 *
 * Deliberately does NOT short-circuit on `areTilesLoaded()`. Immediately after a
 * `fitBounds` the map has not yet started fetching the new tiles, so that check
 * returns true describing the *previous* view — and the caller then reads back
 * an empty chart and concludes there is no charted water. `idle` is the only
 * signal that means what is wanted here.
 */
function waitForMapIdle(map: MapLibreMap, timeoutMs = 10_000): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      map.off('idle', done);
      resolve();
    };
    map.once('idle', done);
    setTimeout(done, timeoutMs);
  });
}


export function App(): React.JSX.Element {
  const [settings, setSettings] = useState<Settings>(loadSettings);
  const [manifest, setManifest] = useState<ChartManifest | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [overzoomed, setOverzoomed] = useState(false);
  const [view, setView] = useState<{
    zoom: number;
    scaleDenominator: number;
    region: Region;
  } | null>(null);
  const [showOffline, setShowOffline] = useState(false);
  const [tapped, setTapped] = useState<{ features: MapGeoJSONFeature[]; lat: number; lon: number } | null>(null);
  const [webcam, setWebcam] = useState<Webcam | null>(null);
  const [harbour, setHarbour] = useState<Harbour | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [route, setRoute] = useState<Route | null>(null);
  const [savedRoutes, setSavedRoutes] = useState<Route[]>([]);
  const [routeCheck, setRouteCheck] = useState<RouteCheckResult | null>(null);
  const [autoRouteStatus, setAutoRouteStatus] = useState<string | null>(null);
  const [autoRouteBusy, setAutoRouteBusy] = useState(false);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [shareNotice, setShareNotice] = useState<string | null>(null);
  /** The track being recorded. Kept here, not in the panel, so closing the
   *  panel does not stop the recording. */
  const [track, setTrack] = useState<Track | null>(null);
  /** A saved track opened to look at. Never recorded into. */
  const [viewedTrack, setViewedTrack] = useState<Track | null>(null);
  const [savedTracks, setSavedTracks] = useState<TrackSummary[]>([]);
  const [showTracks, setShowTracks] = useState(false);
  /** Set once when a background save fails, so the warning is said once. */
  const trackSaveFailed = useRef(false);
  /** Same, for settings — see the effect below. */
  const settingsSaveFailed = useRef(false);
  const [ahead, setAhead] = useState<ForwardScan>({ dangers: [], total: 0 });
  /**
   * Where the simulated boat has been put, and when it last reported.
   *
   * The position is deliberately NOT persisted: a simulated fix restored on
   * startup would be a made-up position presented as a fix, which is the worst
   * thing this app could do. The timestamp is bumped on a timer so the fix does
   * not age into the stale-fix warning while it is being looked at.
   */
  const [simPosition, setSimPosition] = useState<LatLon | null>(null);
  const [simStamp, setSimStamp] = useState(() => Date.now());

  /**
   * Run the depth check.
   *
   * The route is fitted to the view first: `queryRenderedFeatures` can only see
   * tiles loaded for the current viewport, so a leg off screen cannot be
   * checked. Fitting turns "could not check" into "checked" for most routes, and
   * the result reports honestly when it still could not.
   */
  const runRouteCheck = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !manifest || !route || route.waypoints.length < 2) return;

    const bounds = boundsOf(route.waypoints);
    if (bounds) {
      map.fitBounds(
        [[bounds.west, bounds.south], [bounds.east, bounds.north]],
        { padding: 60, duration: 0, maxZoom: 15 },
      );
      // Wait for the newly-requested tiles. See waitForMapIdle for why this
      // must not short-circuit on areTilesLoaded().
      await waitForMapIdle(map);
    }

    setRouteCheck(checkRouteAgainstChart(
      map, route, settings.boat, manifest, settings.units.depth,
    ));
  }, [manifest, route, settings.boat, settings.units.depth]);

  // A stale result is worse than none: editing the route or changing the boat
  // invalidates whatever the last check concluded.
  useEffect(() => { setRouteCheck(null); }, [route?.waypoints, settings.boat]);

  /**
   * Suggest legs that stay in navigable water.
   *
   * Each existing leg is planned separately, so the skipper's own waypoints are
   * kept and only intermediate points are inserted. The result is offered, not
   * applied silently — Undo reverts it, and the depth check is what vouches for
   * it. This is the feature that competing apps get wrong by routing over land.
   */
  const runAutoRoute = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !manifest || !route || route.waypoints.length < 2) return;

    setAutoRouteBusy(true);
    setAutoRouteStatus(null);

    // Restore the skipper's view afterwards; planning moves the map around.
    const originalCentre = map.getCenter();
    const originalZoom = map.getZoom();

    try {
      const options = {
        ...DEFAULT_AUTO_ROUTE_OPTIONS,
        cellSizeM: settings.autoRoute.cellSizeM,
        shoreClearanceCells: settings.autoRoute.shoreClearanceCells,
      };

      const planned: typeof route.waypoints = [];
      const failures: string[] = [];
      const legCount = route.waypoints.length - 1;
      let inserted = 0;

      for (let i = 0; i < legCount; i++) {
        const start = route.waypoints[i];
        const end = route.waypoints[i + 1];
        planned.push(start);

        setAutoRouteStatus(`Planning leg ${i + 1} of ${legCount}…`);

        // Fit the PLANNING BOX, not the leg. The planner reads back rendered
        // tiles, so anything off screen reads as uncharted and therefore as
        // land. Fitting only the leg leaves the padded surroundings — and the
        // endpoints themselves, which sit on the box edge — outside the view.
        const box = planningViewportFor(start, end, options);
        map.fitBounds(
          [[box.west, box.south], [box.east, box.north]],
          { padding: 20, duration: 0, maxZoom: 16 },
        );
        await waitForMapIdle(map);

        const suggestion = suggestRouteBetween(
          map, manifest, settings.boat, start, end, options,
        );
        if (suggestion.failure) {
          failures.push(`Leg ${i + 1}: ${describeAutoRouteFailure(suggestion.failure)}`);
          continue;
        }
        for (const position of suggestion.waypoints) {
          planned.push({ id: newId(), ...position });
          inserted += 1;
        }
      }
      planned.push(route.waypoints[route.waypoints.length - 1]);

      map.easeTo({ center: originalCentre, zoom: originalZoom, duration: 0 });

      if (inserted > 0) {
        setRoute((current) => (current
          ? { ...current, waypoints: planned, updatedAt: Date.now() }
          : current));
      }

      setAutoRouteStatus(
        failures.length > 0
          ? `${inserted} waypoint${inserted === 1 ? '' : 's'} added. ${failures[0]}`
          : inserted === 0
            ? 'Every leg is already clear of land — nothing to add.'
            : `Added ${inserted} waypoint${inserted === 1 ? '' : 's'}. Check the route before using it.`,
      );
    } catch {
      map.easeTo({ center: originalCentre, zoom: originalZoom, duration: 0 });
      setAutoRouteStatus('Could not plan a route here.');
    } finally {
      setAutoRouteBusy(false);
    }
  }, [manifest, route, settings.boat, settings.autoRoute]);

  const refreshSavedRoutes = useCallback(async () => {
    try {
      setSavedRoutes(await listRoutes());
    } catch {
      // A browser with IndexedDB blocked still navigates; it just cannot store.
      setSavedRoutes([]);
    }
  }, []);

  const refreshTracks = useCallback(async () => {
    try {
      setSavedTracks(await listTracks());
    } catch {
      setSavedTracks([]);
    }
  }, []);

  const geolocation = useGeolocation(true);
  useWakeLock(settings.keepScreenAwake);

  /**
   * Persist the settings, and say so if they cannot be persisted.
   *
   * Private browsing, a full quota and a browser set to block site data all make
   * this fail. Failing silently means the skipper sets a draft, watches the chart
   * re-shade, and gets the old draft back next launch — with the chart
   * re-shading to match, which reads as the app deciding on its own that the boat
   * draws less than it does. Said once, and only once: it will fail on every
   * keystroke thereafter and a banner per keystroke is its own problem.
   */
  useEffect(() => {
    if (saveSettings(settings)) return;
    if (settingsSaveFailed.current) return;
    settingsSaveFailed.current = true;
    setShareNotice(
      'Settings cannot be saved on this device — private browsing or blocked '
      + 'site data. They will work for now and be forgotten when you close the app.',
    );
  }, [settings]);

  useEffect(() => {
    let cancelled = false;
    loadChartCollections()
      .then(({ collections, failures }) => {
        if (cancelled) return;
        if (collections.length === 0) {
          setChartError(
            failures[0]?.message ?? 'No chart collections could be loaded.',
          );
          return;
        }
        setManifest(mergeCollections(collections));
        // A region that failed to load looks exactly like water with nothing
        // charted in it, so the failure has to be said out loud.
        if (failures.length > 0) {
          setShareNotice(
            `Could not load ${failures.map((f) => f.id).join(', ')}. `
            + 'Those areas will show as blank chart.',
          );
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setChartError(error instanceof ChartLoadError ? error.message : 'Charts could not be loaded.');
      });
    return () => { cancelled = true; };
  }, []);

  // A shared route arrives in the URL fragment. Import it once at startup and
  // clear the fragment so a later reload does not silently replace edits.
  useEffect(() => {
    const token = extractShareToken(window.location.hash);
    if (!token) return;
    let cancelled = false;

    decodeTokenToRoute(token)
      .then((decoded) => {
        if (cancelled) return;
        const now = Date.now();
        setRoute({
          ...createRoute(newId(), decoded.name || 'Shared route', now),
          waypoints: decodedToWaypoints(decoded, () => newId()),
        });
        setShareNotice(`Opened shared route “${decoded.name || 'Untitled'}”.`);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setShareNotice(
          error instanceof ShareDecodeError
            ? error.message
            : 'That share link could not be read.',
        );
      });

    return () => { cancelled = true; };
  }, []);

  const update = useCallback(<K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Turning simulation on with nowhere to put the boat would do nothing visible
  // until the first tap, which reads as a broken setting. Start it where the
  // skipper is already looking.
  useEffect(() => {
    if (!settings.debug.simulate || simPosition) return;
    const centre = mapRef.current?.getCenter();
    if (centre) {
      setSimPosition({ lat: centre.lat, lon: centre.lng });
      setSimStamp(Date.now());
    }
  }, [settings.debug.simulate, simPosition]);



  const safety = safetyDepth(settings.boat);

  // --- Simulated position (debug) -----------------------------------------
  // Refresh the timestamp so a simulated fix does not age into the stale-fix
  // warning. Ten seconds is comfortably inside STALE_FIX_MS and cheap.
  useEffect(() => {
    if (!settings.debug.simulate) return undefined;
    const timer = setInterval(() => setSimStamp(Date.now()), 10_000);
    return () => clearInterval(timer);
  }, [settings.debug.simulate]);

  /**
   * Advance the simulated boat along its heading.
   *
   * A stationary simulated boat shows the cone and the marks in it perfectly
   * well, and says nothing at all about the proximity alert: a rising click
   * rate and a deepening tint are both *changes*, and there is no change from a
   * boat parked beside a rock. One second per second at the set speed, which is
   * also what makes the forward scan re-run — it fires on map idle, and the map
   * only settles because the boat moved.
   */
  useEffect(() => {
    if (!settings.debug.simulate || !settings.debug.underway) return undefined;
    const timer = setInterval(() => {
      setSimPosition((current) => (current
        ? destinationPoint(current, settings.debug.headingDeg, settings.debug.speedMs)
        : current));
      setSimStamp(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [
    settings.debug.simulate,
    settings.debug.underway,
    settings.debug.headingDeg,
    settings.debug.speedMs,
  ]);

  const simFix = useMemo<Fix | null>(() => (
    settings.debug.simulate && simPosition
      ? {
        lat: simPosition.lat,
        lon: simPosition.lon,
        // A simulated fix has no measurement error to report. Zero draws no
        // accuracy ring, which is one more way it does not look like a real one.
        accuracyM: 0,
        courseDeg: settings.debug.headingDeg,
        speedMs: settings.debug.speedMs,
        timestamp: simStamp,
      }
      : null
  ), [settings.debug, simPosition, simStamp]);

  const fix = simFix ?? geolocation.fix;
  const stale = simFix === null && isStale(fix);

  // --- Track recording -----------------------------------------------------
  /**
   * Append the current fix to the track being recorded.
   *
   * `appendPoint` returns the *same object* when the fix says nothing new, so
   * this settles instead of looping: the state update is a no-op, React bails
   * out of the re-render, and the effect does not fire again until the next fix.
   * The thinning rules live in core — see src/core/track.ts.
   *
   * A simulated fix marks the track, permanently. A recording that is part
   * invented is marked invented; there is no way to un-mark one, and that is
   * the whole reason the flag exists.
   */
  useEffect(() => {
    if (!track || !fix) return;
    setTrack((current) => {
      if (!current) return current;
      const next = appendPoint(current, {
        lat: fix.lat,
        lon: fix.lon,
        t: fix.timestamp,
        speedMs: fix.speedMs,
        courseDeg: fix.courseDeg,
      });
      if (next === current) return current;
      return simFix ? markSimulated(next) : next;
    });
  }, [fix, track, simFix]);

  // Save as it records rather than only on stop. A phone that dies four hours
  // into a passage should cost the last few seconds, not the passage.
  //
  // Not until there is something to save: writing the empty track created on
  // start would leave a row in the saved list for a recording that caught
  // nothing, which `stopTrack` deliberately declines to keep.
  useEffect(() => {
    if (!track || track.points.length === 0) return;
    saveTrack(track).catch(() => {
      if (trackSaveFailed.current) return;
      trackSaveFailed.current = true;
      setShareNotice(
        'Could not save the track being recorded. It is still being kept in '
        + 'memory, but it will be lost if the app is closed.',
      );
    });
  }, [track]);

  const startTrack = useCallback((): void => {
    const now = Date.now();
    const created = createTrack(
      newId(),
      new Date(now).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      }),
      now,
    );
    trackSaveFailed.current = false;
    setViewedTrack(null);
    setTrack(simFix ? markSimulated(created) : created);
  }, [simFix]);

  const stopTrack = useCallback(async (): Promise<void> => {
    if (!track) return;
    // A recording that caught nothing is not worth a row in the list. Anything
    // with a point in it is kept, however short — that is the skipper's call to
    // make with the delete button, not this function's.
    if (track.points.length > 0) await saveTrack(track);
    setTrack(null);
    await refreshTracks();
  }, [track, refreshTracks]);

  /**
   * Open a saved track and put it on screen.
   *
   * Fitted to the view, because a track recorded in Chester tells you nothing
   * while the chart is showing Halifax, and an empty chart is exactly what a
   * failed load looks like.
   */
  const openTrack = useCallback(async (id: string): Promise<void> => {
    const loaded = await loadTrack(id);
    setViewedTrack(loaded);
    const bounds = loaded && trackBounds(loaded);
    if (bounds && mapRef.current) {
      mapRef.current.fitBounds(
        [[bounds.west, bounds.south], [bounds.east, bounds.north]],
        { padding: 60, duration: 0, maxZoom: 15 },
      );
      update('followVessel', false);
    }
  }, [update]);

  const removeTrack = useCallback(async (id: string): Promise<void> => {
    await deleteTrack(id);
    setViewedTrack((current) => (current?.id === id ? null : current));
    await refreshTracks();
  }, [refreshTracks]);

  /** What the chart draws: the live recording wins over anything opened. */
  const trackPoints = track?.points ?? viewedTrack?.points ?? NO_TRACK_POINTS;

  // Course is only meaningful once moving; below the threshold the receiver is
  // taking a bearing between two nearly identical positions. Same threshold as
  // the prediction vector, so the readout and the chart cannot disagree about
  // whether the boat is going anywhere.
  const course = useMemo(() => (
    fix?.courseDeg !== null && fix?.courseDeg !== undefined
      && (fix.speedMs ?? 0) >= MIN_PREDICTION_SPEED_MS
      ? Math.round(fix.courseDeg).toString().padStart(3, '0')
      : null
  ), [fix]);

  const positionLabel = useMemo(() => {
    if (!fix) return null;
    return `${formatLatitude(fix.lat)}   ${formatLongitude(fix.lon)}`;
  }, [fix]);

  // --- Proximity alert -----------------------------------------------------
  // One level behind both the sound and the colour, so they cannot disagree
  // with each other or with the banner above them. Everything it knows comes
  // from the same scan the chart marks come from, which means quiet is only
  // ever "nothing found in the loaded chart ahead".
  const proximity = useMemo(
    () => proximityAlert(ahead.dangers, {
      includeCautions: settings.alerts.includeCautions,
      stages: settings.alerts.stages,
    }),
    [ahead.dangers, settings.alerts.includeCautions, settings.alerts.stages],
  );
  const alarm = useProximityAlarm(settings.alerts.sound, proximity, settings.alerts.volume);

  /**
   * The last few seconds, said as a flash rather than as more of the same.
   *
   * A rising rate and a deepening colour convey a trend; by the time the trend
   * has run out there is nothing left for it to do. A flash is a different kind
   * of signal, and that is the point — it cannot be read as more of the previous
   * one. Dangers only: floating over a shoal is not a collision.
   */
  const collision = useMemo(
    () => isCollision(proximity, settings.alerts.collision),
    [proximity, settings.alerts.collision],
  );

  // Before anything else, including a chart-loading failure. What the app does
  // not know — no tide correction, true bearings only — has to be read before
  // the chart is on screen to be argued with, not after.
  if (needsAcknowledgement(settings.disclaimerAcknowledgedVersion)) {
    return (
      <DisclaimerGate
        onAccept={() => update('disclaimerAcknowledgedVersion', DISCLAIMER_VERSION)}
      />
    );
  }

  if (chartError) {
    return (
      <div className="fatal">
        <h1>Charts unavailable</h1>
        <p>{chartError}</p>
      </div>
    );
  }

  if (!manifest) {
    return <div className="loading" role="status">Loading charts…</div>;
  }

  return (
    <div
      className={`app app--${settings.palette}${
        settings.alerts.tint && settings.alerts.tintStyle === 'bar' ? ' app--bar-alert' : ''
      }`}
    >
      <ChartView
        manifest={manifest}
        boat={settings.boat}
        palette={settings.palette}
        showSoundings={settings.showSoundings}
        showLabels={settings.showLabels}
        showWebcams={settings.showWebcams}
        showHarbours={settings.showHarbours}
        prediction={settings.prediction}
        dangersAhead={settings.dangersAhead}
        cautionDepthM={settings.cautionDepthM}
        coneAngleDeg={settings.coneAngleDeg}
        depthUnit={settings.units.depth}
        distanceUnit={settings.units.distance}
        onDangersAhead={setAhead}
        onViewChange={setView}
        onMapReady={(map) => { mapRef.current = map; }}
        fix={fix}
        followVessel={settings.followVessel}
        onUserPan={() => update('followVessel', false)}
        onOverzoomChange={setOverzoomed}
        routeWaypoints={route?.waypoints ?? []}
        trackPoints={trackPoints}
        onFeatureTap={(features, position) => {
          // In route mode a tap places a waypoint. Identifying features and
          // building a route are different intents, and conflating them is one
          // of the things users complain about in other apps.
          if (route) {
            setRoute((current) => (current
              ? addWaypoint(current, { id: newId(), ...position })
              : current));
            return;
          }

          // Debug: a tap moves the simulated boat. Route mode still wins, so a
          // route can be built while simulating; identifying a feature does not,
          // which is why the setting says so and why it is off by default.
          if (settings.debug.simulate) {
            setTapped(null);
            setWebcam(null);
            setHarbour(null);
            setSimPosition(position);
            setSimStamp(Date.now());
            return;
          }

          // Annotation markers are small and deliberately aimed at, so they
          // take the tap ahead of whatever charted feature lies under them.
          const camera = features
            .map((feature) => feature.properties?.webcamId)
            .filter((id): id is string => typeof id === 'string')
            .map(webcamById)
            .find(Boolean);
          if (camera) {
            setTapped(null);
            setHarbour(null);
            setWebcam(camera);
            return;
          }

          const berth = features
            .map((feature) => feature.properties?.harbourId)
            .filter((id): id is string => typeof id === 'string')
            .map(harbourById)
            .find(Boolean);
          if (berth) {
            setTapped(null);
            setWebcam(null);
            setHarbour(berth);
            return;
          }

          setWebcam(null);
          setHarbour(null);
          setTapped({ features, ...position });
        }}
      />

      {/* The alert as a colour. Two shapes, differing in how much chart they are
          willing to touch.

          `bar` is a strip across the very top, over nothing at all — it cannot
          shift a single charted colour, which is why it is the default. `screen`
          is the vignette: the middle keeps its true colours and red creeps in
          from the edges. Both sit below the chrome in the stack, so a banner and
          the readout stay at full contrast at any level. Presentational only —
          the banner says the same thing in words. */}
      {settings.alerts.tint && proximity.level > 0 && (() => {
        const style = settings.alerts.tintStyle;
        const peak = tintOpacity(proximity.level, style);
        // The flash animates opacity, which would otherwise be pinned by the
        // inline style. `--flash-peak` hands the keyframes the level-derived
        // strength so a flashing alert is not a *different* brightness from a
        // steady one — it is the same colour, interrupted.
        const flash = collision
          ? {
            animationDuration: `${flashPeriodMs(settings.alerts.collision.flashHz)}ms`,
            ['--flash-peak' as string]: String(peak),
          }
          : {};

        return style === 'bar' ? (
          <div
            className={collision ? 'alert-bar alert-bar--flash' : 'alert-bar'}
            aria-hidden="true"
            style={{
              opacity: peak,
              background: tintColour(proximity.severity),
              ...flash,
            }}
          />
        ) : (
          <div
            className={collision ? 'alert-tint alert-tint--flash' : 'alert-tint'}
            aria-hidden="true"
            style={{
              opacity: peak,
              background: `radial-gradient(ellipse at center, transparent ${
                TINT_CLEAR_RADIUS_PCT
              }%, ${tintColour(proximity.severity)} 100%)`,
              ...flash,
            }}
          />
        );
      })()}

      <div className="banners">
        {/* What is charted in the sector ahead. The marks on the chart are the
            primary display; this says how many there are and names the nearest,
            because the nearest is the one there is time to do something about. */}
        {ahead.dangers.length > 0 && (
          <div
            className={ahead.total > 0 ? 'banner banner--danger' : 'banner banner--caution'}
            role="alert"
          >
            <strong>
              {/* A caution is not a danger and must never be counted as one —
                  that count is what keeps the magenta ring meaning something. */}
              {ahead.total === 0
                ? 'SHOAL AHEAD'
                : ahead.total === 1 ? 'DANGER AHEAD' : `${ahead.total} DANGERS AHEAD`}
            </strong>
            <span>
              {describeForwardDanger(
                // Describe the nearest DANGER when there is one: a headline
                // counting dangers beside a sentence about passable water reads
                // as though the app cannot tell them apart.
                ahead.dangers.find((danger) => danger.severity === 'danger') ?? ahead.dangers[0],
                settings.units.distance,
                settings.units.depth,
                settings.boat,
              )}
            </span>
          </div>
        )}
        {/* A simulated position is not a fix. This is undismissable on purpose:
            an app quietly showing a made-up position is the worst thing here. */}
        {simFix && (
          <div className="banner banner--warn" role="alert">
            <strong>SIMULATED</strong>
            <span>
              Position, course and speed are made up. Tap the chart to move the
              boat; turn this off in Settings → Debug.
            </span>
          </div>
        )}
        {settings.debug.simulate && !simFix && (
          <div className="banner banner--info" role="status">
            <span>Simulation on — tap the chart to place the boat.</span>
          </div>
        )}
        {/* Over-scaling is a documented grounding cause: symbols stop covering
            the ground they represent. This warning is not dismissible. */}
        {overzoomed && (
          <div className="banner banner--warn" role="alert">
            <strong>OVERZOOM</strong>
            <span>
              Beyond chart scale (max z{maxChartedZoom(manifest)}). Hazards may not cover their true extent.
            </span>
          </div>
        )}
        {geolocation.message && (
          <div className="banner banner--info" role="status">
            <span>{geolocation.message}</span>
            {geolocation.status === 'denied' && (
              <button type="button" onClick={geolocation.retry}>Retry</button>
            )}
          </div>
        )}
        {stale && (
          <div className="banner banner--warn" role="alert">
            <strong>STALE FIX</strong>
            <span>Position has not updated recently.</span>
          </div>
        )}
        {shareNotice && (
          <div className="banner banner--info" role="status">
            <span>{shareNotice}</span>
            <button type="button" onClick={() => setShareNotice(null)}>Dismiss</button>
          </div>
        )}
      </div>

      <div className="readout">
        <div className="readout__safety">
          <span className="readout__label">Safety depth</span>
          <span className="readout__value">{formatDepth(safety, settings.units.depth)}</span>
        </div>
        {/* Speed over ground, from the receiver. Shown as a dash rather than as
            zero when the device does not report it: a confident 0.0 kn on a
            moving boat is worse than an admission of ignorance. */}
        <div className="readout__speed">
          <span className="readout__label">SOG</span>
          <span className="readout__value">
            {fix?.speedMs !== null && fix?.speedMs !== undefined
              ? formatSpeed(fix.speedMs, settings.units.speed)
              : '—'}
          </span>
          {course !== null && <span className="readout__course">{course}°T</span>}
        </div>
        {view && (
          <div className={`readout__scale${overzoomed ? ' readout__scale--over' : ''}`}>
            <span className="readout__label">Scale</span>
            <span className="readout__value readout__value--small">
              {formatScale(view.scaleDenominator)}
            </span>
            <span className="readout__zoom">z{view.zoom.toFixed(1)}</span>
          </div>
        )}
        {positionLabel ? (
          <div className={`readout__position${isPoorAccuracy(fix) ? ' readout__position--poor' : ''}`}>
            {positionLabel}
            {fix && <span className="readout__accuracy">±{Math.round(fix.accuracyM)} m</span>}
          </div>
        ) : (
          <div className="readout__position readout__position--none">No position fix</div>
        )}
      </div>

      <div className="controls">
        <button
          type="button"
          className={settings.followVessel ? 'control control--on' : 'control'}
          onClick={() => update('followVessel', !settings.followVessel)}
          disabled={!fix}
          aria-pressed={settings.followVessel}
        >
          Follow
        </button>
        <button
          type="button"
          className="control"
          onClick={() => update('palette', settings.palette === 'day' ? 'night' : 'day')}
        >
          {settings.palette === 'day' ? 'Night' : 'Day'}
        </button>
        <button
          type="button"
          className={settings.showSoundings ? 'control control--on' : 'control'}
          onClick={() => update('showSoundings', !settings.showSoundings)}
          aria-pressed={settings.showSoundings}
        >
          Depths
        </button>
        <button
          type="button"
          className={route ? 'control control--on' : 'control'}
          onClick={() => {
            setTapped(null);
            setShowTracks(false);
            setRoute((current) => (current ? null : createRoute(newId(), 'New route')));
            void refreshSavedRoutes();
          }}
          aria-pressed={route !== null}
        >
          Route
        </button>
        {/* Recording continues whether or not this panel is open — the state
            lives in App. The button reads as on while a track is recording,
            not merely while the panel is showing. */}
        <button
          type="button"
          className={track ? 'control control--rec' : 'control'}
          onClick={() => {
            setShowTracks((v) => !v);
            setShowSettings(false);
            setShowOffline(false);
            void refreshTracks();
          }}
          aria-expanded={showTracks}
        >
          {track ? '● Track' : 'Track'}
        </button>
        {/* Labels are toggled from Settings → Display. The bottom bar is for
            things reached one-handed underway; a labels toggle is not one. */}
        <button
          type="button"
          className={showOffline ? 'control control--on' : 'control'}
          onClick={() => { setShowOffline((v) => !v); setShowSettings(false); setShowTracks(false); }}
          aria-expanded={showOffline}
        >
          Offline
        </button>
        <button
          type="button"
          className="control"
          onClick={() => { setShowSettings((v) => !v); setShowOffline(false); setShowTracks(false); }}
          aria-expanded={showSettings}
        >
          Settings
        </button>
      </div>

      {showSettings && (
        <SettingsPanel
          settings={settings}
          onChange={setSettings}
          onClose={() => setShowSettings(false)}
          onTestSound={alarm.test}
          alarm={alarm}
        />
      )}

      {showOffline && (
        <OfflinePanel
          manifest={manifest}
          currentRegion={view?.region ?? null}
          onClose={() => setShowOffline(false)}
        />
      )}

      {/* The sheets share one slot on screen. Opening tracks hides the route
          panel without ending route mode — the route stays on the chart and
          still takes taps. */}
      {showTracks && (
        <TrackPanel
          recording={track}
          viewing={viewedTrack}
          saved={savedTracks}
          units={settings.units}
          simulating={simFix !== null}
          noFix={fix === null}
          onStart={startTrack}
          onStop={stopTrack}
          onRename={(name) => setTrack((t) => (t ? { ...t, name, updatedAt: Date.now() } : t))}
          onOpen={openTrack}
          onCloseViewed={() => setViewedTrack(null)}
          onDelete={removeTrack}
          onClose={() => setShowTracks(false)}
        />
      )}

      {route && !showTracks && (
        <RoutePanel
          route={route}
          boat={settings.boat}
          units={settings.units}
          onRename={(name) => setRoute((r) => (r ? { ...r, name, updatedAt: Date.now() } : r))}
          onUndo={() => setRoute((r) => (r
            ? { ...r, waypoints: r.waypoints.slice(0, -1), updatedAt: Date.now() }
            : r))}
          onClear={() => setRoute((r) => (r ? { ...r, waypoints: [], updatedAt: Date.now() } : r))}
          onReverse={() => setRoute((r) => (r ? reverseRoute(r) : r))}
          savedRoutes={savedRoutes}
          check={routeCheck}
          onCheck={() => { void runRouteCheck(); }}
          autoRouteStatus={autoRouteStatus}
          autoRouteBusy={autoRouteBusy}
          onAutoRoute={() => { void runAutoRoute(); }}
          onSave={async () => {
            if (!route) return;
            await saveRoute(route);
            await refreshSavedRoutes();
          }}
          onOpen={(saved) => setRoute(saved)}
          onDelete={async (id) => {
            await deleteRoute(id);
            await refreshSavedRoutes();
          }}
          onClose={() => setRoute(null)}
        />
      )}

      {webcam && !route && !showTracks && (
        <WebcamPanel webcam={webcam} onClose={() => setWebcam(null)} />
      )}

      {harbour && !route && !showTracks && (
        <HarbourPanel harbour={harbour} onClose={() => setHarbour(null)} />
      )}

      {tapped && !route && !showTracks && (
        <FeaturePanel
          features={tapped.features}
          position={{ lat: tapped.lat, lon: tapped.lon }}
          units={settings.units}
          boat={settings.boat}
          onClose={() => setTapped(null)}
        />
      )}
    </div>
  );
}
